import React, { useState, useMemo } from 'react';
import { track, useTrackScreen } from '@/lib/analytics';
import {
  View,
  Text,
  ScrollView,
  } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams } from 'expo-router';
import AnimatedPressable from '@/components/AnimatedPressable';
import { ScreenFrame } from '@/components/ScreenFrame';
import {
  MessageCircle,
  TrendingUp,
  Building2,
} from 'lucide-react-native';
import Animated, { FadeInUp, FadeIn } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { SectionDivider } from '@/components/SectionDivider';
import type {
  Conversation,
  Message,
} from '@/lib/state/messaging-store';
import { useProfileStore } from '@/lib/state/profile-store';
import { useMyDataRoomAccessRequests } from '@/lib/supabase/api';
import { requestStateFor } from '@/lib/dd/dd-entry';
import { ACCESS_REJECTED_LINE } from '@/lib/messaging/system-lines';
import { useSubscriptionStore } from '@/lib/state/subscription-store';
import { isWeb } from '@/lib/platform';
import { usePhoneShell } from '@/lib/useShell';
import { mode } from '@/lib/config/app-mode';
import { AppLayout } from '@/components/AppLayout';
import { RealtimeChat, READ_ONLY_SEND_COPY } from '@/components/RealtimeChat';
import { isRelationshipDenied, withPublicCardNames } from '@/lib/supabase/messaging-mappers';
import { publicCardKeys, useCompanyPublicCards } from '@/lib/company/public-card';
import { formatConversationTime } from '@/lib/format';
import { useQueryClient } from '@tanstack/react-query';
import { UpgradePrompt } from '@/components/subscription';
import { UsageIndicator } from '@/components/subscription/UsageIndicator';
import { useAlert } from '@/components/AlertDialog';
import { handleError, getUserMessage } from '@/lib/errors';
import { isObjectionableContentError } from '@/lib/moderation/api';
import {
  useRealtimeConversations,
  usePresence,
  useConversations,
  useMessages,
  useSendMessage,
  mapConversationRow,
  mapMessageRow,
  isSupabaseConfigured,
} from '@/lib/supabase';

// Mock conversations for demo
const mockConversations: Conversation[] = [
  {
    id: 'conv-1',
    startupId: 'startup-1',
    startupName: 'TechVenture AI',
    investorId: 'inv-1',
    investorProfileId: 'inv-1',
    investorName: 'Sarah Chen',
    // A read mark far in the future so sample mode demonstrates the RECEIPT
    // rather than sitting permanently on "Sent", which is also what a broken
    // receipt looks like. The counterparty side is the one that matters: an
    // investor reads startup_last_read_at, a founder reads investor_last_read_at.
    startupLastReadAt: '2099-01-01T00:00:00.000Z',
    investorLastReadAt: '2099-01-01T00:00:00.000Z',
    lastMessage: 'We have 15 qualified leads in our pipeline.',
    lastMessageTime: '2 hours ago',
    unreadCount: 1,
    status: 'active',
  },
  {
    id: 'conv-4',
    startupId: 'startup-2',
    startupName: 'GreenEnergy Solutions',
    investorId: 'inv-1',
    investorProfileId: 'inv-1',
    investorName: 'Sarah Chen',
    lastMessage: 'Thank you for reviewing our pitch deck!',
    lastMessageTime: 'Yesterday',
    unreadCount: 0,
    status: 'active',
  },
  {
    id: 'conv-5',
    startupId: 'startup-3',
    startupName: 'HealthTech Pro',
    investorId: 'inv-1',
    investorProfileId: 'inv-1',
    investorName: 'Sarah Chen',
    lastMessage: 'Our Series A is progressing well.',
    lastMessageTime: '2 days ago',
    unreadCount: 0,
    status: 'active',
  },
];

const mockMessages: Message[] = [
  {
    id: 'msg-1',
    conversationId: 'conv-1',
    senderId: 'inv-1',
    senderName: 'You',
    senderType: 'investor',
    text: 'Hi! I reviewed your pitch deck and I have some questions about your go-to-market strategy.',
    timestamp: '10:30 AM',
  },
  {
    id: 'msg-2',
    conversationId: 'conv-1',
    senderId: 'startup-1',
    senderName: 'John (Founder)',
    senderType: 'startup',
    senderRole: 'founder',
    text: 'Thank you for your interest! Happy to discuss. We are focusing on enterprise customers initially.',
    timestamp: '10:45 AM',
  },
  {
    id: 'msg-3',
    conversationId: 'conv-1',
    senderId: 'inv-1',
    senderName: 'You',
    senderType: 'investor',
    text: 'That makes sense. What is your current pipeline looking like?',
    timestamp: '11:00 AM',
  },
  {
    id: 'msg-4',
    conversationId: 'conv-1',
    senderId: 'startup-1',
    senderName: 'Maria (IR)',
    senderType: 'startup',
    senderRole: 'investor_relations',
    text: 'We have 15 qualified leads in our pipeline with 3 in final negotiations.',
    timestamp: '11:15 AM',
  },
];

export default function InvestorMessagesScreen() {
  useTrackScreen('InvestorMessages');
  const colors = useThemeColors();
  const phoneShell = usePhoneShell();
  // THE OPEN CONVERSATION IS A ROUTE, NOT STATE. See the founder-side screen for
  // the full reasoning: state gave the thread no URL, so a notification could not
  // link to it, a refresh lost your place, and nothing automated could reach it.
  //
  // `setParams` rather than `push`: addressable without adding a history entry or a
  // second instance of a tab screen. Back does not close the thread, which is
  // exactly today's behaviour — this adds addressability and regresses nothing.
  const router = useRouter();
  const { conversation: openConversationId } = useLocalSearchParams<{ conversation?: string }>();
  const [isSending, setIsSending] = useState(false);
  // Conversations the investor has opened this session — used to optimistically
  // zero the unread badge before the realtime UPDATE / refetch lands (and to
  // keep the "mark read on open" UX in sample mode, which has no backend).
  const [locallyReadIds, setLocallyReadIds] = useState<Set<string>>(new Set());
  // Local echo of messages sent while in sample/demo mode (no backend to write to).
  const [localMessages, setLocalMessages] = useState<Message[]>([]);

  const investorProfile = useProfileStore((s) => s.investorProfile);
  const investorProfileId = investorProfile?.id;
  const user = useProfileStore((s) => s.user);
  const { error: showError } = useAlert();
  const sendMessage = useSendMessage();
  const queryClient = useQueryClient();

  // Feature gating - messaging requires Standard+
  const hasMessaging = useSubscriptionStore((s) => s.hasFeature('direct_messaging'));

  // Real-time subscriptions
  useRealtimeConversations(investorProfileId, 'investor');

  // Real conversations (production) — snake→camel via mapConversationRow.
  const { data: conversationRows } = useConversations(investorProfileId, 'investor');
  const realConversations = useMemo(
    () => (conversationRows ?? []).map((row) => mapConversationRow(row)),
    [conversationRows]
  );
  const sealedCards = useCompanyPublicCards(
    realConversations.filter((c) => c.startupNameMissing).map((c) => c.startupId),
  );

  // Real data in production; mock arrays ONLY behind mode.showSampleData.
  const displayConversations = useMemo(() => {
    const base = realConversations.length > 0
      ? realConversations
      : (mode.showSampleData ? mockConversations : []);
    // Optimistically clear the unread badge for conversations opened this session.
    const read = base.map((c) => (locallyReadIds.has(c.id) ? { ...c, unreadCount: 0 } : c));
    // 119-07: a sealed company's name comes from its public card.
    return withPublicCardNames(read, sealedCards.data);
  }, [realConversations, locallyReadIds, sealedCards.data]);


  // Derived, so the route is the single source of truth. An id matching nothing —
  // a stale link, or a conversation this investor cannot see — resolves to null and
  // shows the list rather than an empty thread.
  const selectedConversation = useMemo(
    () => displayConversations.find((c) => c.id === openConversationId) ?? null,
    [displayConversations, openConversationId]
  );

  // Real messages for the open conversation.
  const { data: messageRows } = useMessages(selectedConversation?.id);

  /**
   * The rejection notice, DERIVED from the request row rather than stored as a
   * message. Shown only in the investor's thread, which is where it is
   * addressed — the founder does not need to be told their own answer.
   */
  const { data: myAccessRequests = [] } = useMyDataRoomAccessRequests(investorProfileId);
  const systemNotice =
    requestStateFor(selectedConversation?.startupId, myAccessRequests) === 'rejected'
      ? ACCESS_REJECTED_LINE
      : null;
  const realMessages = useMemo(
    () => (messageRows ?? []).map(mapMessageRow),
    [messageRows]
  );

  // Presence tracking for online indicators - use user.name for personal name
  const { isUserOnline } = usePresence(
    investorProfileId && user ? { id: investorProfileId, name: user.name } : null,
    'global'
  );

  const conversationMessages = useMemo(() => {
    if (!selectedConversation) return [];
    if (realMessages.length > 0) return realMessages;
    const local = localMessages.filter((m) => m.conversationId === selectedConversation.id);
    if (mode.showSampleData) {
      const base = mockMessages.filter((m) => m.conversationId === selectedConversation.id);
      return [...base, ...local];
    }
    return local;
  }, [selectedConversation, realMessages, localMessages]);

  const totalUnread = displayConversations.reduce((sum, c) => sum + c.unreadCount, 0);

  const handleSelectConversation = (conv: Conversation) => {
    track('conversation_opened', { conversationId: conv.id });
    router.setParams({ conversation: conv.id });
    // Read-marking against the backend happens inside RealtimeChat
    // (markMessagesAsRead) for real conversations; here we just optimistically
    // zero the badge so the list reflects the open state immediately.
    if (conv.unreadCount > 0) {
      setLocallyReadIds((prev) => new Set(prev).add(conv.id));
    }
  };

  const handleSendMessage = async (text: string) => {
    if (!selectedConversation) return;

    setIsSending(true);
    try {
      if (mode.isMock || !isSupabaseConfigured()) {
        // Sample/demo mode: no backend — echo the message locally so the UX works.
        await new Promise((resolve) => setTimeout(resolve, 300));
        const localMsg: Message = {
          id: `msg-${Date.now()}`,
          conversationId: selectedConversation.id,
          senderId: user?.id ?? investorProfileId ?? 'inv-1',
          senderName: 'You',
          senderType: 'investor',
          text,
          // An ISO instant, like every server-written message — the bubble
          // formats it at render. This used to be pre-formatted here, which is
          // why `timestamp` carried two different shapes.
          timestamp: new Date().toISOString(),
        };
        setLocalMessages((prev) => [...prev, localMsg]);
      } else {
        // Production: write to Supabase. sender_id MUST be the auth uid — the
        // messages RLS INSERT policy checks sender_id = auth.uid(). Realtime
        // (useRealtimeMessages in RealtimeChat) reflects it back into the list.
        await sendMessage.mutateAsync({
          conversation_id: selectedConversation.id,
          sender_id: user?.id ?? '',
          sender_type: 'investor',
          text,
        });
      }
      track('message_sent', { conversationId: selectedConversation.id });
    } catch (err) {
      // The objectionable-text filter trigger is an expected, user-actionable
      // rejection (not a bug) — show its readable sentence directly instead of
      // routing it through handleError, which would both report it to Sentry
      // as a genuine error and fall back to the generic "unexpected error" copy.
      if (isObjectionableContentError(err)) {
        showError('Message Failed', getUserMessage(err));
      } else if (isRelationshipDenied(err)) {
        // A read-only thread (the company closed, owner decision #9) refuses
        // the insert with 42501 — expected, so not reported. Refreshing the
        // public card swaps the composer for the read-only notice.
        void queryClient.invalidateQueries({ queryKey: publicCardKeys.all });
        showError('Message Not Sent', READ_ONLY_SEND_COPY);
      } else {
        const { message, isCancelled } = handleError(err, {
          context: 'InvestorMessages.handleSendMessage',
        });
        if (!isCancelled) showError('Message Failed', message);
      }
    } finally {
      setIsSending(false);
    }
  };

  // Chat View - use RealtimeChat component
  if (selectedConversation) {
    return (
      <AppLayout title={selectedConversation.startupName} constrainWidth={false}>
        <RealtimeChat
          conversation={selectedConversation}
          messages={conversationMessages}
          currentUser={{
            // Auth uid — markMessagesAsRead / message sender_id are keyed on it.
            id: user?.id || investorProfileId || 'inv-1',
            name: user?.name || 'Investor',
            type: 'investor',
          }}
          onBack={() => router.setParams({ conversation: undefined })}
          onSendMessage={handleSendMessage}
          systemNotice={systemNotice}
          isSending={isSending}
        />
      </AppLayout>
    );
  }

  // Show upgrade prompt if user doesn't have messaging access
  if (!hasMessaging) {
    return (
      <AppLayout title="Messages">
        <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
          <SafeAreaView edges={isWeb ? [] : ['top']} className="flex-1">
            <ScrollView
              className="flex-1"
              contentContainerStyle={{ flexGrow: 1, justifyContent: 'center', padding: 20 }}
              showsVerticalScrollIndicator={false}
            >
              <View className="items-center mb-8">
                <View
                  className="w-20 h-20 rounded-full items-center justify-center mb-4"
                  style={{ backgroundColor: colors.primary[100] }}
                >
                  <MessageCircle size={40} color={colors.primary[600]} />
                </View>
                <Text
                  style={{ color: colors.text.primary }}
                  className="text-2xl font-bold text-center mb-2"
                >
                  Direct Messaging
                </Text>
                <Text
                  style={{ color: colors.text.secondary }}
                  className="text-center px-4"
                >
                  Connect directly with founders and discuss investment opportunities
                </Text>
              </View>
              <UpgradePrompt
                targetTier="standard"
                variant="fullscreen"
                headline="Unlock Founder Messaging"
                lockedTitle="Founder messaging"
                maxFeatures={4}
              />
            </ScrollView>
          </SafeAreaView>
        </View>
      </AppLayout>
    );
  }

  // Conversation List View
  return (
    <AppLayout title="Messages">
      <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
        {/* Pinned, and carrying the global controls — see the note on score.tsx.
           The usage meter moved into the body: it is descriptive metadata, and a
           navigation row that also has to hold a growing counter is the row that
           squeezes the controls out. No bottomEdge: tab screen. */}
        <ScreenFrame
          eyebrow="Investor Tools"
          title="Messages"
          actions={<ScreenFrame.GlobalActions />}
        >
          <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
            {phoneShell && (
              <View className="px-5 pb-2">
                <UsageIndicator feature="direct_messaging" suffix="/mo" />
              </View>
            )}
            {/* Stats */}
            <Animated.View
              entering={FadeInUp.delay(200).duration(500)}
              className="px-5 mb-6"
            >
              <View className="flex-row gap-3">
                <View
                  className="flex-1 rounded-2xl p-4"
                  style={{
                    backgroundColor: colors.surface.primary,
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.05,
                    shadowRadius: 8,
                    elevation: 2,
                  }}
                >
                  <MessageCircle size={20} color={colors.primary[600]} />
                  <Text style={{ color: colors.text.primary }} className="text-2xl font-bold mt-2">
                    {displayConversations.length}
                  </Text>
                  <Text style={{ color: colors.text.tertiary }} className="text-xs">Conversations</Text>
                </View>
                <View
                  className="flex-1 rounded-2xl p-4"
                  style={{
                    backgroundColor: totalUnread > 0 ? colors.traffic.greenLight : colors.surface.primary,
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.05,
                    shadowRadius: 8,
                    elevation: 2,
                  }}
                >
                  <TrendingUp size={20} color={totalUnread > 0 ? colors.success : colors.text.tertiary} />
                  <Text
                    className="text-2xl font-bold mt-2"
                    style={{ color: totalUnread > 0 ? colors.success : colors.text.primary }}
                  >
                    {totalUnread}
                  </Text>
                  <Text style={{ color: colors.text.tertiary }} className="text-xs">Unread</Text>
                </View>
              </View>
            </Animated.View>

            {/* Conversations List */}
            <Animated.View
              entering={FadeInUp.delay(300).duration(500)}
              className="px-5 pb-8"
            >
              <SectionDivider label="Startup Conversations" marginTop={0} marginBottom={16} />
              {displayConversations.length > 0 ? (
                <View className="gap-3">
                  {displayConversations.map((conv, index) => (
                    <Animated.View
                      key={conv.id}
                      entering={FadeIn.delay(300 + index * 50).duration(300)}
                    >
                      <AnimatedPressable
                        onPress={() => handleSelectConversation(conv)}
                        className="rounded-2xl overflow-hidden"
                        style={{
                          backgroundColor: colors.surface.primary,
                          shadowColor: '#000',
                          shadowOffset: { width: 0, height: 1 },
                          shadowOpacity: 0.08,
                          shadowRadius: 4,
                          elevation: 2,
                        }}
                      >
                        <View className="p-4">
                          <View className="flex-row items-center gap-3">
                            {/* Avatar with online indicator */}
                            <View className="relative">
                              <View
                                className="w-12 h-12 rounded-full items-center justify-center"
                                style={{ backgroundColor: colors.traffic.greenLight }}
                              >
                                <Building2 size={22} color={colors.success} />
                              </View>
                              {/* Online indicator */}
                              <View
                                className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2"
                                style={{
                                  backgroundColor: isUserOnline(conv.startupId)
                                    ? colors.traffic.green
                                    : colors.text.tertiary,
                                  borderColor: colors.surface.primary,
                                }}
                              />
                            </View>
                            <View className="flex-1">
                              <View className="flex-row items-center justify-between" style={{ gap: 8 }}>
                                <Text style={{ color: colors.text.primary }} className="font-semibold flex-1" numberOfLines={1}>{conv.startupName}</Text>
                                <Text style={{ color: colors.text.tertiary }} className="text-xs shrink-0" numberOfLines={1}>
                                  {formatConversationTime(conv.lastMessageTime)}
                                </Text>
                              </View>
                              <View className="flex-row items-center justify-between mt-1">
                                <Text
                                  style={{ color: conv.unreadCount > 0 ? colors.text.primary : colors.text.secondary }}
                                  className="text-sm flex-1"
                                  numberOfLines={1}
                                >
                                  {conv.lastMessage}
                                </Text>
                                {conv.unreadCount > 0 && (
                                  <View
                                    className="w-6 h-6 rounded-full items-center justify-center ml-2"
                                    style={{ backgroundColor: colors.primary[600] }}
                                  >
                                    <Text style={{ color: colors.text.inverse }} className="text-xs font-bold">
                                      {conv.unreadCount}
                                    </Text>
                                  </View>
                                )}
                              </View>
                            </View>
                          </View>
                        </View>
                      </AnimatedPressable>
                    </Animated.View>
                  ))}
                </View>
              ) : (
                <View
                  className="rounded-2xl p-8 items-center"
                  style={{
                    backgroundColor: colors.surface.primary,
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 1 },
                    shadowOpacity: 0.05,
                    shadowRadius: 4,
                    elevation: 1,
                  }}
                >
                  <MessageCircle size={48} color={colors.text.tertiary} />
                  <Text style={{ color: colors.text.secondary }} className="text-center mt-4">
                    No conversations yet.{'\n'}Start a conversation from dealflow.
                  </Text>
                </View>
              )}
            </Animated.View>

            {/* Info Note */}
            <Animated.View
              entering={FadeInUp.delay(400).duration(500)}
              className="px-5 pb-8"
            >
              <View
                className="rounded-2xl p-4"
                style={{
                  backgroundColor: colors.surface.primary,
                  borderWidth: 1,
                  borderColor: colors.border.light,
                }}
              >
                <Text style={{ color: colors.text.primary }} className="text-sm font-medium mb-2">
                  Team Members
                </Text>
                <Text style={{ color: colors.text.secondary }} className="text-xs">
                  Startup teams may have multiple members responding: Founders, Co-Founders,
                  and Investor Relations staff. All responses are clearly labeled.
                </Text>
              </View>
            </Animated.View>
          </ScrollView>
        </ScreenFrame>
      </View>
    </AppLayout>
  );
}
